/**
 * Cálculo da premiação do bolão Copa 2026 com regras de faltas.
 *
 * Regras (espelham o que o organizador definiu):
 *
 * 1. Prêmios base:
 *    - 1º lugar: R$ 2.400,00
 *    - 2º lugar: R$ 600,00
 *    - 3º em diante: R$ 0 (só ganham se receberem repasse de cima).
 *
 *    A ordem é sempre por PONTUAÇÃO atual (não fixa). Se a tabela muda
 *    porque alguém ultrapassou outro nos pontos, todo o cálculo é refeito
 *    automaticamente — o 1º e o 2º base mudam de pessoa, e os descontos
 *    por falta seguem aplicados na nova posição.
 *
 * 1b. Empate de pontuação (prêmio base ajustado):
 *    - Quando 2+ pessoas estão com a mesma pontuação total, elas
 *      "ocupam" as mesmas posições da tabela (empate).
 *    - Soma os prêmios base das posições que esse grupo ocupa e
 *      divide igualmente entre os empatados. Esse valor já dividido
 *      vira o NOVO prêmio base de cada um, antes da regra de faltas.
 *    - Exemplos:
 *        • 2 empatados em 1º (ocupam 1º+2º) → cada um recebe (2.400+600)/2 = 1.500.
 *        • 3 empatados em 1º (ocupam 1º+2º+3º) → cada um recebe (2.400+600+0)/3 = 1.000.
 *        • 1 sozinho 1º + 2 empatados em 2º (ocupam 2º+3º) → 1º=2.400; cada empatado=(600+0)/2=300.
 *    - O empate só altera o passo 1. As regras de faltas, cascata e
 *      sobras (passos 2..5) seguem aplicadas em cima do prêmio base
 *      ajustado, exatamente como antes.
 *
 * 2. Faltas (1 dia = 9 horas) entre 11/06 e 19/07 tiram 25% do prêmio
 *    por falta (cumulativo, até zerar):
 *    - 1 falta = -25%; 2 faltas = -50%; 3 = -75%; 4+ = -100%.
 *    O desconto incide tanto sobre o PRÊMIO BASE (já ajustado por empate)
 *    quanto sobre VALORES RECEBIDOS por repasse (faltante também pode
 *    receber, mas perde a mesma % sobre o que entrar pra ele).
 *
 * 3. Redistribuição do valor perdido (regra de "abrir novos premiados"):
 *    - O valor perdido é dividido em pedaços iguais:
 *      • Se ≤ R$ 75, fica inteiro como 1 pedaço só.
 *      • Se > R$ 75, divide em N partes iguais, em que N é o menor número
 *        que faça CADA PARTE ≤ R$ 600 (o prêmio do 2º). Limite mínimo
 *        de 2 partes. Ou seja, nenhum novo premiado leva mais que o 2º:
 *          - 1º faltou 1 dia → perdeu R$ 600 → 2 partes (R$ 300 cada).
 *          - 1º faltou 2 dias → perdeu R$ 1.200 → 2 partes (R$ 600 cada).
 *          - 1º faltou 3 dias → perdeu R$ 1.800 → 3 partes (R$ 600 cada).
 *          - 1º faltou 4+ dias → perdeu R$ 2.400 → 4 partes (R$ 600 cada).
 *    - O 1º distribui PRIMEIRO. Depois o 2º. Etc.
 *    - Cada origem distribui descendo a tabela e dando preferência para
 *      o PRÓXIMO PARTICIPANTE QUE AINDA NÃO TEM PRÊMIO. Quem já tem
 *      prêmio base (1º, 2º, ou os empatados que dividiram esse prêmio
 *      base) NÃO recebe repasse direto — só pode ganhar mais via sobra
 *      (passo 4), quando o valor chega ao fim da tabela e volta. Quem
 *      já recebeu direto numa rodada também não recebe de novo direto.
 *      Quem tem 4+ faltas (100% de desconto) é PULADO porque não
 *      consegue receber nada de qualquer forma.
 *    - Faltantes (com menos de 4 faltas) podem ser destino, desde que
 *      ainda não tenham prêmio base. Quando recebem, eles também perdem
 *      a sua % (faltas × 25%) sobre o valor recebido; esse novo valor
 *      perdido entra na próxima rodada de redistribuição.
 *
 * 4. Sobras (nunca "evaporam"):
 *    - Pedaço sem destino volta para o topo da tabela: vai para o
 *      NÃO-FALTANTE mais bem colocado. A próxima sobra vai para o
 *      próximo não-faltante. Se todos já receberam sobra, dá voltas.
 *    - Só evapora se NÃO houver nenhum não-faltante na lista.
 *
 * 5. Regra de consistência (entre NÃO-faltantes):
 *    - Quem está em colocação melhor recebe prêmio igual ou maior
 *      que quem está embaixo.
 *    - Faltantes mantêm o prêmio que sobrou — não são "puxados pra
 *      cima" para igualar não-faltantes de baixo.
 *    - Empatados em pontos ficam com prêmios iguais (já garantido
 *      pelo passo 1b).
 */

export const PREMIO_BASE_1 = 2400;
export const PREMIO_BASE_2 = 600;
/** Limiar pra dividir o valor perdido em 2 (acima deste valor divide; igual/abaixo vai inteiro). */
export const LIMIAR_DIVIDIR = 75;

export interface EntradaPremiacao {
  id: string;
  nome: string;
  faltas: number;
  /**
   * Pontos do participante (opcional). Quando informado, dois ou mais
   * participantes com a MESMA pontuação são considerados empatados — o
   * prêmio base das posições que ocupam é somado e dividido igualmente
   * entre eles antes da regra de faltas. Se omitido, comportamento
   * idêntico ao antigo (sem empates).
   */
  pontos?: number;
}

export interface ResultadoPremiacao {
  id: string;
  nome: string;
  posicao: number;
  faltas: number;
  premioBase: number;
  premioFinal: number;
  perdidoPorFalta: number;
  recebidoDeOutros: number;
  detalheRepasses: { de: string; valor: number }[];
  observacao: string;
}

function descontoPorFaltas(faltas: number): number {
  if (faltas <= 0) return 0;
  return Math.min(1, 0.25 * faltas);
}

/**
 * Divide o valor em N partes iguais, onde N é o menor número que faz
 * cada parte ≤ PREMIO_BASE_2 (R$ 600). Limite mínimo de 2 partes.
 * Se ≤ LIMIAR_DIVIDIR (R$ 75), vai inteiro num pedaço só.
 */
function gerarPedacos(valor: number): number[] {
  if (valor <= LIMIAR_DIVIDIR) return [valor];
  let partes = 2;
  const MAX_PARTES = 10; // proteção contra valores absurdamente altos
  while (valor / partes > PREMIO_BASE_2 && partes < MAX_PARTES) {
    partes++;
  }
  const valorPorParte = valor / partes;
  return new Array<number>(partes).fill(valorPorParte);
}

/**
 * Calcula a premiação para uma lista de participantes já ordenados
 * por classificação no bolão (1º lugar primeiro).
 *
 * @param participantes Lista ordenada por posição no bolão. Apenas os 2 primeiros
 *                      recebem prêmio base; os demais só podem receber repasse.
 */
export function calcularPremiacao(
  participantes: EntradaPremiacao[]
): ResultadoPremiacao[] {
  const n = participantes.length;
  if (n === 0) return [];

  const premios = new Array<number>(n).fill(0);
  const perdido = new Array<number>(n).fill(0);
  const recebido = new Array<number>(n).fill(0);
  const fontes: { de: string; valor: number }[][] = Array.from(
    { length: n },
    () => []
  );

  // 1. Prêmio base só para 1º e 2º (ordem já vem por pontuação)
  //    Quando há empate em pontos, o prêmio base das posições do grupo
  //    é somado e dividido igualmente entre os empatados.
  const premiosBaseRaw = new Array<number>(n).fill(0);
  if (n >= 1) premiosBaseRaw[0] = PREMIO_BASE_1;
  if (n >= 2) premiosBaseRaw[1] = PREMIO_BASE_2;

  // grupoDeIdx[i] = id do grupo empatado a que o índice i pertence
  // (ou -1 se está sozinho). Usado para a cascata pular outros
  // empatados do mesmo grupo da origem.
  const grupoDeIdx = new Array<number>(n).fill(-1);

  // Detecta empates apenas se o campo `pontos` foi informado.
  const algumPontos = participantes.some((p) => typeof p.pontos === 'number');
  if (algumPontos) {
    let i = 0;
    let proxGrupoId = 0;
    while (i < n) {
      const ponto = participantes[i].pontos;
      let j = i;
      // Empate só se os dois lados têm `pontos` definido e iguais.
      while (
        j + 1 < n &&
        typeof participantes[j + 1].pontos === 'number' &&
        typeof ponto === 'number' &&
        participantes[j + 1].pontos === ponto
      ) {
        j++;
      }
      const tamanho = j - i + 1;
      if (tamanho > 1) {
        // Soma os prêmios base das posições i..j e divide igualmente.
        let soma = 0;
        for (let k = i; k <= j; k++) soma += premiosBaseRaw[k];
        const valorIgual = soma / tamanho;
        const grupoId = proxGrupoId++;
        for (let k = i; k <= j; k++) {
          premios[k] = valorIgual;
          grupoDeIdx[k] = grupoId;
        }
      } else {
        premios[i] = premiosBaseRaw[i];
      }
      i = j + 1;
    }
  } else {
    for (let i = 0; i < n; i++) premios[i] = premiosBaseRaw[i];
  }

  const idxNaoFaltantes: number[] = [];
  for (let i = 0; i < n; i++) {
    if (participantes[i].faltas === 0) idxNaoFaltantes.push(i);
  }

  type Repasse = { de: string; valor: number; origemIdx: number };

  // (Snapshot) Quem teve prêmio BASE (incluindo empatados que dividiram
  // o prêmio base): esses NÃO recebem repasse direto na cascata. Só
  // podem ganhar mais via sobra (passo 4).
  const teveBase = premios.map((v) => v > 0);

  // 2. Pilha inicial: desconto por falta sobre o prêmio BASE
  let pilha: Repasse[] = [];
  for (let i = 0; i < n; i++) {
    if (premios[i] <= 0) continue;
    const desc = descontoPorFaltas(participantes[i].faltas);
    if (desc <= 0) continue;
    const valorPerdido = premios[i] * desc;
    perdido[i] += valorPerdido;
    premios[i] -= valorPerdido;
    pilha.push({
      de: participantes[i].nome,
      valor: valorPerdido,
      origemIdx: i,
    });
  }

  // 3. Distribuição em cascata, iterativa.
  //    Cada origem divide o pedaço (>75 → /2; ≤75 → inteiro) e aloca
  //    para o PRÓXIMO PARTICIPANTE QUE AINDA NÃO RECEBEU DIRETO
  //    nessa rodada. Quem já recebeu direto não recebe de novo.
  //    Quem tem 4+ faltas (100% de desconto) é PULADO porque não
  //    consegue receber nada de qualquer forma.
  //    Faltantes (com menos de 4 faltas) PODEM ser destino. Quando
  //    recebem, sofrem desconto sobre o valor recebido, e esse novo
  //    valor perdido entra na próxima rodada.
  const podeReceber = (idx: number) => participantes[idx].faltas < 4;
  const MAX_RODADAS = 12; // proteção contra divisão infinita em casos extremos
  const sobrasAcumuladas: { de: string; valor: number }[] = [];
  const jaRecebeuDireto = new Array<boolean>(n).fill(false);
  // Quem tem prêmio base já é "premiado" — repasse direto pula esses.
  // Eles só podem ganhar mais via sobra (passo 4), quando o valor cai
  // no fim da tabela e volta para o topo.
  for (let i = 0; i < n; i++) {
    if (teveBase[i]) jaRecebeuDireto[i] = true;
  }

  for (let rodada = 0; rodada < MAX_RODADAS && pilha.length > 0; rodada++) {
    pilha.sort((a, b) => a.origemIdx - b.origemIdx);
    const recebidoNessaRodada = new Array<number>(n).fill(0);

    for (const item of pilha) {
      const pedacos = gerarPedacos(item.valor);

      // Começa imediatamente depois da origem (não pula i+1 mais).
      // Pula também membros do MESMO grupo empatado da origem
      // (para não premiar um empatado em detrimento do outro).
      const grupoOrigem = grupoDeIdx[item.origemIdx];
      let destino = item.origemIdx + 1;
      for (const pedaco of pedacos) {
        while (
          destino < n &&
          (destino === item.origemIdx ||
            jaRecebeuDireto[destino] ||
            !podeReceber(destino) ||
            (grupoOrigem !== -1 && grupoDeIdx[destino] === grupoOrigem))
        ) {
          destino++;
        }
        if (destino >= n) {
          sobrasAcumuladas.push({ de: item.de, valor: pedaco });
          continue;
        }
        jaRecebeuDireto[destino] = true;
        recebidoNessaRodada[destino] += pedaco;
        premios[destino] += pedaco;
        recebido[destino] += pedaco;
        fontes[destino].push({ de: item.de, valor: pedaco });
        destino++;
      }
    }

    // Faltantes que receberam nessa rodada perdem % sobre o que receberam
    const novaPilha: Repasse[] = [];
    for (let i = 0; i < n; i++) {
      if (recebidoNessaRodada[i] <= 0) continue;
      const desc = descontoPorFaltas(participantes[i].faltas);
      if (desc <= 0) continue;
      const perdaExtra = recebidoNessaRodada[i] * desc;
      premios[i] -= perdaExtra;
      perdido[i] += perdaExtra;
      novaPilha.push({
        de: participantes[i].nome,
        valor: perdaExtra,
        origemIdx: i,
      });
    }
    pilha = novaPilha;
  }

  // 4. Sobras: nunca evaporam. Vão para o NÃO-FALTANTE de colocação mais alta,
  //    depois o próximo, dando voltas se necessário.
  //    Quando uma sobra cai num grupo empatado, ela é dividida igualmente
  //    entre TODOS os membros não-faltantes do grupo, para não quebrar
  //    a igualdade de prêmio dentro do empate.
  if (sobrasAcumuladas.length > 0 && idxNaoFaltantes.length > 0) {
    // Pré-calcula, para cada índice não-faltante, a lista de membros
    // não-faltantes do mesmo grupo empatado (incluindo ele mesmo).
    const naoFaltantesPorGrupo = new Map<number, number[]>();
    for (const idx of idxNaoFaltantes) {
      const g = grupoDeIdx[idx];
      if (g === -1) continue;
      if (!naoFaltantesPorGrupo.has(g)) naoFaltantesPorGrupo.set(g, []);
      naoFaltantesPorGrupo.get(g)!.push(idx);
    }
    for (let k = 0; k < sobrasAcumuladas.length; k++) {
      const idxAlvo = idxNaoFaltantes[k % idxNaoFaltantes.length];
      const grupo = grupoDeIdx[idxAlvo];
      const grupoMembros = grupo !== -1 ? naoFaltantesPorGrupo.get(grupo) : null;
      if (grupoMembros && grupoMembros.length > 1) {
        // Divide sobra igualmente entre os membros não-faltantes do grupo
        const parte = sobrasAcumuladas[k].valor / grupoMembros.length;
        for (const idx of grupoMembros) {
          premios[idx] += parte;
          recebido[idx] += parte;
          fontes[idx].push({
            de: sobrasAcumuladas[k].de,
            valor: parte,
          });
        }
      } else {
        const idx = idxAlvo;
        premios[idx] += sobrasAcumuladas[k].valor;
        recebido[idx] += sobrasAcumuladas[k].valor;
        fontes[idx].push({ de: sobrasAcumuladas[k].de, valor: sobrasAcumuladas[k].valor });
      }
    }
  }

  // 5. Regra de consistência: ordena os prêmios entre os NÃO-faltantes
  //    de forma decrescente, MAS preserva a ordem dentro de cada grupo
  //    empatado em pontos (membros do mesmo grupo não trocam de posição
  //    entre si, pois empate não pode "subir" alguém pela cascata).
  //    Faltantes ficam fixos em suas posições com o valor que sobrou
  //    após as faltas.
  if (idxNaoFaltantes.length > 1) {
    // Posições não-faltantes que estão FORA de qualquer grupo empatado
    // (ou que estão sozinhas no grupo) participam da reordenação.
    const idxOrdenaveis: number[] = [];
    for (const i of idxNaoFaltantes) {
      if (grupoDeIdx[i] === -1) idxOrdenaveis.push(i);
    }
    if (idxOrdenaveis.length > 1) {
      const snapshot = idxOrdenaveis.map((i) => ({
        premio: premios[i],
        perdido: perdido[i],
        recebido: recebido[i],
        fontes: fontes[i],
      }));
      snapshot.sort((a, b) => b.premio - a.premio);
      for (let k = 0; k < idxOrdenaveis.length; k++) {
        const idx = idxOrdenaveis[k];
        premios[idx] = snapshot[k].premio;
        perdido[idx] = snapshot[k].perdido;
        recebido[idx] = snapshot[k].recebido;
        fontes[idx] = snapshot[k].fontes;
      }
    }
  }

  // 6. Monta saída
  // Calcula o "prêmio base ajustado" de cada um (após divisão por empate,
  // mas antes da regra de faltas). Soma os prêmios base raw do grupo
  // empatado e divide pelo tamanho dele.
  const premioBaseAjustado = new Array<number>(n).fill(0);
  {
    let i = 0;
    while (i < n) {
      const g = grupoDeIdx[i];
      if (g === -1) {
        premioBaseAjustado[i] = premiosBaseRaw[i];
        i++;
        continue;
      }
      let j = i;
      while (j + 1 < n && grupoDeIdx[j + 1] === g) j++;
      let soma = 0;
      for (let k = i; k <= j; k++) soma += premiosBaseRaw[k];
      const valor = soma / (j - i + 1);
      for (let k = i; k <= j; k++) premioBaseAjustado[k] = valor;
      i = j + 1;
    }
  }

  return participantes.map((p, i) => {
    const obs: string[] = [];
    const ehEmpatado = grupoDeIdx[i] !== -1;
    if (ehEmpatado && premiosBaseRaw[i] !== premioBaseAjustado[i]) {
      obs.push(
        `empate: prêmio base ajustado para ${formatarReais(premioBaseAjustado[i])}`
      );
    }
    if (p.faltas > 0) {
      const pct = Math.round(descontoPorFaltas(p.faltas) * 100);
      obs.push(`${p.faltas} falta${p.faltas > 1 ? 's' : ''} (-${pct}%)`);
    }
    if (recebido[i] > 0) {
      obs.push(`recebeu repasse de ${formatarReais(recebido[i])}`);
    }
    if (
      !ehEmpatado &&
      i < 2 &&
      p.faltas === 0 &&
      perdido[i] === 0 &&
      fontes[i].length === 0
    ) {
      obs.push('sem alterações');
    }
    return {
      id: p.id,
      nome: p.nome,
      posicao: i + 1,
      faltas: p.faltas,
      premioBase: Math.round(premioBaseAjustado[i] * 100) / 100,
      premioFinal: Math.round(premios[i] * 100) / 100,
      perdidoPorFalta: Math.round(perdido[i] * 100) / 100,
      recebidoDeOutros: Math.round(recebido[i] * 100) / 100,
      detalheRepasses: fontes[i],
      observacao: obs.join(' · ') || '—',
    };
  });
}

/** Formata em "R$ 0,00" (português Brasil). */
export function formatarReais(v: number): string {
  return v.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
